/** The value actions of the MCP diagnose review pages — one family, one links module (the
 *  detail-page value actions rule). Every href comes from `gridLinks`; `mcpDiagnoseRunLinks.test.ts`
 *  proves each one lands on a real route. Filter links write the grid's own URL params (`tenant`,
 *  `review`, `reason`, `judge`, `outcome`, `from` / `to`), so the toolbar shows the filter they set. */
import {
  judgeLabel,
  REVIEW_AUDIT_EVENT,
  reviewReasonLabel,
  type McpDiagnoseJudgeOutcome,
  type McpDiagnoseReview,
} from '@/api/mcpDiagnoseRuns';
import type { GridLink } from '@/components/ValueActions/identity';
import { act, type ValueAction } from '@/components/ValueActions/ValueActions';
import { auditHref, mcpDiagnoseRunsHref } from '@/utils/gridLinks';

/** One run's page. */
export const mcpDiagnoseRunHref = (taskId: string) =>
  `/mcp-diagnose-runs/${encodeURIComponent(taskId)}`;

/** The review queue: flagged runs nobody reviewed yet. */
export const reviewQueueHref = () => mcpDiagnoseRunsHref({ review: 'pending' });

/** The console audit log's reviews of a tenant's runs (the audit rows carry the tenant). */
export const reviewsAuditHref = (tenantId: number) =>
  auditHref({ event_contains: REVIEW_AUDIT_EVENT, tenant: tenantId });

/** The operator part of a header: "Diagnose runs of this operator". */
export const tenantGrid = (tenantId: number): GridLink => ({
  href: mcpDiagnoseRunsHref({ tenant: tenantId }),
  label: 'Diagnose runs of this operator',
});

export function runActions(taskId: string): ValueAction[] {
  return [
    act.open(mcpDiagnoseRunHref(taskId), 'Open this run'),
    act.open(`/bgtasks/${encodeURIComponent(taskId)}`, 'Open its background task'),
    act.copy(taskId, 'Copy run id'),
  ];
}

/** The booking the client asked about. */
export function bookingActions(bookingId: number): ValueAction[] {
  return [act.open(`/bookings/${bookingId}`, 'Open booking'), act.copy(String(bookingId), 'Copy booking id')];
}

export function judgeActions(outcome: McpDiagnoseJudgeOutcome | 'none'): ValueAction[] {
  return [
    act.filter(
      mcpDiagnoseRunsHref({ judge: outcome }),
      `Only runs the judge marked ${judgeLabel(outcome).toLowerCase()}`,
    ),
  ];
}

export function reasonActions(reason: McpDiagnoseReview['reason']): ValueAction[] {
  return [
    act.filter(
      mcpDiagnoseRunsHref({ reason }),
      `Only runs flagged: ${reviewReasonLabel(reason).toLowerCase()}`,
    ),
  ];
}

/** The tenant user who asked, through their AI client. */
export function askerActions(user: { id: number | null; email: string }): ValueAction[] {
  return [
    ...(user.id !== null ? [act.open(`/users/${user.id}`, 'Open user')] : []),
    act.filter(auditHref({ actor_email: user.email }), 'Audit entries by this user'),
    act.copy(user.email, 'Copy email'),
  ];
}
